import { default as ArrowDownwardIcon } from '@mui/icons-material/ArrowDownward'
import { default as ArrowUpwardIcon } from '@mui/icons-material/ArrowUpward'
import { default as CheckCircleIcon } from '@mui/icons-material/CheckCircle'
import { default as ClearIcon } from '@mui/icons-material/Clear'
import { default as ExpandLessIcon } from '@mui/icons-material/ExpandLess'
import { default as ExpandMoreIcon } from '@mui/icons-material/ExpandMore'
import { default as FilterListIcon } from '@mui/icons-material/FilterList'
import { default as MoreVertIcon } from '@mui/icons-material/MoreVert'
import { default as OpenInNewIcon } from '@mui/icons-material/OpenInNew'
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Dialog,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { CSmartTable } from '@coreui/react-pro'
import { useCallback, useEffect, useState, useRef } from 'react'
import { toast } from 'react-toastify'
import { getCSmartTableColumnData } from 'src/consumer/helpers/utilFunctions'
import { mediaURL } from 'src/consumer/services'
import {
  getDataMapFromCommonResponse,
  getPriceStringWithCurrency,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import { getSPDataWithParams } from 'src/supportPortal/services/services'
import InvoiceConflictDialog from './InvoiceConflictDialog'
import MarkAsPaidDialog from './MarkAsPaidDialog'
// import DateRangePicker from '@wojtekmaj/react-daterange-picker'
// import '@wojtekmaj/react-daterange-picker/dist/DateRangePicker.css'
// import 'react-calendar/dist/Calendar.css'
import dayjs from 'dayjs'
import { CustomSelect, selectTypes } from 'src/consumer/components/helperComponents'
import InvoiceDetailsDialog from 'src/supportPortal/views/payoutReports/InvoiceDetailsDialog'

const InvoiceListing = ({
  targetEntityType = 'mentor',
  initialFilters = {},
  entityListOverride,
  hideTargetEntityIdFilter = false,
  hideEntityName = false,
  hideMentorProfilePic = false,
  hidePaymentStatusFilter = false,
  hideActions = false,
  bookedEventsListingApiSignature = '/api/secure/cc/support/events/all/paginated',
  invoicesListingApiSignature = '/api/secure/cc/support/invoices/payout-invoice/list',
  prepareBookedEventsDataFromApiResponse,
}) => {
  const [invoices, setInvoices] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingEntities, setLoadingEntities] = useState(false)
  const [totalElements, setTotalElements] = useState(0)
  const [currentPage, setCurrentPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [entities, setEntities] = useState([])
  const [showFilters, setShowFilters] = useState(false)
  const [menuAnchorEl, setMenuAnchorEl] = useState(null)
  const [selectedItem, setSelectedItem] = useState(null)
  const [searchInput, setSearchInput] = useState(initialFilters.search || '') // Local search input state for debouncing
  const [markAsPaidDialogOpen, setMarkAsPaidDialogOpen] = useState(false)
  const [conflictDialogOpen, setConflictDialogOpen] = useState(false)
  const [selectedInvoice, setSelectedInvoice] = useState(null)
  const [pendingPaymentInfo, setPendingPaymentInfo] = useState(null)
  const [showInvoiceDetailsDialog, setShowInvoiceDetailsDialog] = useState(false)

  // Filter states
  const [filters, setFilters] = useState({
    targetEntityTypes: targetEntityType,
    targetEntityIds: '',
    paymentStatuses: '',
    invoiceType: '',
    invoiceNumber: '',
    search: '',
    ...initialFilters,
  })
  const [dateRange, setDateRange] = useState(null) // [startDate, endDate] or null
  const [sortField, setSortField] = useState(null)
  const [sortDirection, setSortDirection] = useState('asc') // 'asc' or 'desc'

  // Track if this is the initial render
  const isInitialRender = useRef(true)

  // Define sort fields with their labels
  const sortFields = [
    { field: 'invoiceFrom', label: 'Invoice From' },
    { field: 'invoiceTo', label: 'Invoice To' },
    { field: 'invoicingDate', label: 'Invoicing Date' },
    { field: 'dueDate', label: 'Invoice Due Date' },
    { field: 'paymentDate', label: 'Payment Date' },
    { field: 'createdTime', label: 'Generated On' },
  ]

  // Fetch entities based on entity type
  const fetchEntities = useCallback(async () => {
    setLoadingEntities(true)
    try {
      let endpoint = ''
      if (targetEntityType === 'mentor') {
        endpoint = '/api/secure/cc/support/therapists/clinical/list'
      } else if (targetEntityType === 'clinic-brand') {
        endpoint = '/api/secure/cc/support/clinics-with-brands/list'
      }

      if (endpoint) {
        const response = await getSPDataWithParams(endpoint, {})
        if (response.status === 200 && isCommonResponseSuccessfulV2(response)) {
          if (targetEntityType === 'mentor') {
            setEntities(getDataMapFromCommonResponse(response)?.['mentor'] || [])
          } else {
            const clinicsList = getDataMapFromCommonResponse(response)?.['clinic'] || []
            const uniqueBrands = [
              ...new Map(
                clinicsList?.map((item) => [
                  item?.clinicBrandId,
                  {
                    id: item?.clinicBrandId,
                    title: item?.clinicBrandName,
                    ownerUserIds: item?.clinicBrandOwnerUserIds,
                    slug: item?.clinicBrandSlug,
                  },
                ]),
              ).values(),
            ]
            setEntities(uniqueBrands)
          }
        }
      }
    } catch (error) {
      console.error('Error fetching entities:', error)
      toast.error('Failed to fetch entities')
    } finally {
      setLoadingEntities(false)
    }
  }, [targetEntityType])

  // Fetch invoices
  const fetchInvoices = useCallback(
    async (page = 0) => {
      setLoading(true)
      try {
        const params = {
          page,
          size: pageSize,
          ...filters,
        }

        // Add date range to params if selected
        if (dateRange && dateRange[0] && dateRange[1]) {
          params.fromDate = dayjs(dateRange[0]).format('YYYY-MM-DD')
          params.toDate = dayjs(dateRange[1]).format('YYYY-MM-DD')
        }

        // Add sort parameter if selected
        if (sortField) {
          params.sort = `${sortField},${sortDirection}`
        }

        // Remove empty filters
        Object.keys(params).forEach((key) => {
          if (params[key] === '' || params[key] === null || params[key] === undefined) {
            delete params[key]
          }
        })

        const response = await getSPDataWithParams(invoicesListingApiSignature, params)

        if (response.status === 200 && isCommonResponseSuccessfulV2(response)) {
          const invoicesData = getDataMapFromCommonResponse(response)?.['payout-invoice']
          setInvoices(invoicesData?.content || [])
          setTotalElements(invoicesData?.totalElements || 0)
          setCurrentPage(page)
        } else {
          toast.error(response.data?.message || 'Failed to fetch invoices')
        }
      } catch (error) {
        console.error('Error fetching invoices:', error)
        toast.error('Failed to fetch invoices')
      } finally {
        setLoading(false)
      }
    },
    [filters, pageSize, dateRange, sortField, sortDirection, invoicesListingApiSignature],
  )

  // Handle filter changes
  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }))
  }

  // Handle sort changes - Three state cycle: asc → desc → no sort
  const handleSortChange = (field) => {
    if (sortField === field) {
      // Cycle through states: asc → desc → null
      if (sortDirection === 'asc') {
        setSortDirection('desc')
      } else if (sortDirection === 'desc') {
        // Remove sort
        setSortField(null)
        setSortDirection('asc') // Reset to asc for next time
      }
    } else {
      // Set new field starting with asc
      setSortField(field)
      setSortDirection('asc')
    }
    setCurrentPage(0) // Reset to first page when sorting
  }

  // Clear filters
  const clearFilters = () => {
    setFilters({
      targetEntityTypes: targetEntityType,
      targetEntityIds: '',
      paymentStatuses: '',
      invoiceType: '',
      invoiceNumber: '',
      search: '',
      ...initialFilters,
    })
    setDateRange(null)
    setSearchInput('')
    setSortField(null)
    setSortDirection('asc')
    setCurrentPage(0) // Reset to first page when clearing filters
  }

  // Apply filters
  const applyFilters = () => {
    setCurrentPage(0) // Reset to first page when applying filters
    fetchInvoices(0)
  }

  // Handle menu open
  const handleMenuOpen = (event, item) => {
    event.preventDefault()
    event.stopPropagation()
    setMenuAnchorEl(event.currentTarget)
    setSelectedItem(item)
  }

  // Handle menu close
  const handleMenuClose = () => {
    setMenuAnchorEl(null)
    // setSelectedItem(null)
  }

  // Handle media download/open
  const handleMediaAction = (mediaId, action = 'download') => {
    if (!mediaId) {
      toast.error('No media available')
      return
    }

    const mediaUrl = `${mediaURL}${mediaId}`

    if (action === 'download') {
      // Create a temporary link to download
      const link = document.createElement('a')
      link.href = mediaUrl
      link.download = `invoice-${mediaId}`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } else if (action === 'open') {
      window.open(mediaUrl, '_blank')
    }
  }

  // Handle mark as paid dialog - first check for conflicts
  const handleOpenMarkAsPaidDialog = (invoice) => {
    handleMenuClose() // Close the menu first
    setSelectedInvoice(invoice)

    // Prepare basic payment info for conflict checking
    const basicPaymentInfo = {
      paymentStatus: 'paid',
      paidOn: new Date().toISOString(),
    }
    setPendingPaymentInfo(basicPaymentInfo)
    setConflictDialogOpen(true)
  }

  // Handle conflict resolution - proceed to payment form
  const handleConflictResolved = () => {
    setConflictDialogOpen(false)
    setMarkAsPaidDialogOpen(true)
  }

  // Handle conflict dialog cancellation/regeneration
  const handleConflictCancellation = () => {
    setConflictDialogOpen(false)
    setSelectedInvoice(null)
    setPendingPaymentInfo(null)
    fetchInvoices(currentPage) // Refresh the table
  }

  const handleCloseMarkAsPaidDialog = () => {
    setMarkAsPaidDialogOpen(false)
    setSelectedInvoice(null)
    setPendingPaymentInfo(null)
  }

  const handleCloseConflictDialog = () => {
    setConflictDialogOpen(false)
    setSelectedInvoice(null)
    setPendingPaymentInfo(null)
  }

  const handleMarkAsPaidSuccess = () => {
    fetchInvoices(currentPage) // Refresh the table
  }

  // Get entity name (unused - keeping for potential future use)
  // const getEntityName = (entityId) => {
  //   const entity = entities.find((e) => e.id === entityId)
  //   if (!entity) return entityId

  //   if (targetEntityType === 'mentor') {
  //     return entity.name || entity.username || entityId
  //   } else if (targetEntityType === 'clinic') {
  //     return entity.clinicBrandName || entity.name || entityId
  //   }
  //   return entityId
  // }
  const getEntityObject = (entityId) => {
    const entity = entities.find((e) => e.id === entityId)
    if (!entity) return entityId

    if (targetEntityType === 'mentor') {
      return entity
    } else if (targetEntityType === 'clinic-brand') {
      return entity
    }
    return entityId
  }

  function handleRowClick(item) {
    setSelectedInvoice(item)
    setShowInvoiceDetailsDialog(true)
  }

  function handleCloseInvoiceDetailsDialog() {
    setShowInvoiceDetailsDialog(false)
    setSelectedInvoice(null)
  }

  // Define table columns
  const columns = [
    getCSmartTableColumnData({
      key: 'invoiceDetails',
      label: 'Invoice Details',
      _style: { minWidth: '150px' },
      sorter: false,
    }),
    // getCSmartTableColumnData({
    //   key: 'invoiceType',
    //   label: 'Type',
    //   _style: { minWidth: '100px' },
    //   sorter: false,
    // }),
    getCSmartTableColumnData({
      key: 'period',
      label: 'Period',
      _style: { minWidth: '120px' },
      sorter: false,
    }),
    getCSmartTableColumnData({
      key: 'invoicingDate',
      label: 'Invoice Details',
      _style: { minWidth: '120px' },
      sorter: false,
    }),
    getCSmartTableColumnData({
      key: 'paymentStatus',
      label: 'Payment Status',
      _style: { minWidth: '120px' },
      sorter: false,
    }),
    getCSmartTableColumnData({
      key: 'paymentDetails',
      label: 'Payment Details',
      _style: { minWidth: '120px' },
      sorter: false,
    }),
    ...(hideActions
      ? []
      : [
          getCSmartTableColumnData({
            key: 'actions',
            label: 'Actions',
            _style: { minWidth: '100px' },
            sorter: false,
          }),
        ]),
  ]

  // Define scoped columns for custom rendering
  const scopedColumns = {
    invoiceDetails: (item) => {
      const entity = getEntityObject(item.targetEntityId)
      return (
        <td>
          <div className="d-flex align-items-center gap-1">
            {targetEntityType === 'mentor' && !hideMentorProfilePic ? (
              <Avatar size="small" src={`${mediaURL}${entity.profilePicId}`} />
            ) : (
              <></>
            )}
            <div>
              {!hideEntityName && (
                <div>
                  {loadingEntities ? (
                    <Skeleton variant="text" width={100} />
                  ) : (
                    <>
                      <div>
                        {entity.name ||
                          entity?.title ||
                          entity.username ||
                          entity.clinicBrandName ||
                          entity.name}
                      </div>
                    </>
                  )}
                </div>
              )}
              <div>{item?.invoiceNumber}</div>
            </div>
          </div>
        </td>
      )
    },
    invoiceType: (item) => (
      <td>
        <Chip
          label={item.invoiceType?.charAt(0).toUpperCase() + item.invoiceType?.slice(1)}
          color={item.invoiceType === 'provisional' ? 'warning' : 'info'}
          size="small"
        />
      </td>
    ),
    period: (item) => (
      <td>
        <div className="d-flex flex-column gap-1">
          <span>
            {item.invoiceFrom ? dayjs.utc(item.invoiceFrom).local().format('DD MMM YYYY') : '-'}
            <strong> - </strong>{' '}
            {item.invoiceTo ? dayjs.utc(item.invoiceTo).local().format('DD MMM YYYY') : '-'}
          </span>
          <span>
            <strong>Generated On:</strong>{' '}
            {item.createdTime ? dayjs.utc(item.createdTime).local().format('DD MMM YYYY') : '-'}
          </span>
        </div>
      </td>
    ),
    invoicingDate: (item) => (
      <td>
        <div className="d-flex flex-column gap-1">
          <span>
            <strong>Amount Generated:</strong>{' '}
            {item.payoutAmountGenerated
              ? getPriceStringWithCurrency(item.payoutAmountGenerated)
              : '--'}
          </span>
          <span>
            <strong>Invoicing Date:</strong>{' '}
            {item.invoicingDate ? dayjs.utc(item.invoicingDate).local().format('DD MMM YYYY') : '-'}
          </span>
          <span>
            <strong>Due:</strong>{' '}
            {item.dueDate ? dayjs.utc(item.dueDate).local().format('DD MMM YYYY') : '-'}
          </span>
        </div>
      </td>
    ),
    paymentStatus: (item) => (
      <td>
        <Chip
          label={item.paymentStatus?.charAt(0).toUpperCase() + item.paymentStatus?.slice(1)}
          color={
            item.paymentStatus === 'paid'
              ? 'success'
              : item.paymentStatus === 'pending'
              ? 'warning'
              : 'error'
          }
          size="small"
        />
      </td>
    ),
    paymentDetails: (item) => {
      return (
        <td>
          {item?.paymentInfo ? (
            <div className="d-flex flex-column gap-1">
              {item?.paymentInfo?.paidOn && (
                <span>
                  <strong>Paid On:</strong>{' '}
                  {dayjs.utc(item.paymentInfo.paidOn).isValid()
                    ? dayjs.utc(item.paymentInfo.paidOn).local().format('DD MMM YYYY HH:mm')
                    : item.paymentInfo.paidOn}
                </span>
              )}
              {item?.paymentInfo?.amtPaid && (
                <span>
                  <strong>Amount Paid:</strong>{' '}
                  {getPriceStringWithCurrency(
                    item.paymentInfo.amtPaid,
                    item.paymentInfo.currencyCode,
                  )}
                </span>
              )}
              {/* {item?.paymentInfo?.currencyCode && (
                <span>
                  <strong>Currency:</strong>{' '}
                </span>
              )} */}
              {item?.paymentInfo?.retainerAmt && (
                <span>
                  <strong>Retainer Amount:</strong>{' '}
                  {getPriceStringWithCurrency(
                    item.paymentInfo.retainerAmt,
                    item.paymentInfo.currencyCode,
                  )}
                </span>
              )}
              {item?.paymentInfo?.minimumPayout && (
                <span>
                  <strong>Minimum Payout:</strong>{' '}
                  {getPriceStringWithCurrency(
                    item.paymentInfo.minimumPayout,
                    item.paymentInfo.currencyCode,
                  )}
                </span>
              )}
              {item?.paymentInfo?.paidBy && (
                <span>
                  <strong>Paid By:</strong> {item.paymentInfo.paidBy}
                </span>
              )}
              {item?.paymentInfo?.comments && (
                <span>
                  <strong>Comments:</strong> {item.paymentInfo.comments}
                </span>
              )}
            </div>
          ) : (
            <span>--</span>
          )}
        </td>
      )
    },
    actions: (item) => (
      <td>
        <div className="d-flex justify-content-center align-items-center">
          <IconButton
            size="small"
            onClick={(event) => handleMenuOpen(event, item)}
            aria-label="more actions"
          >
            <MoreVertIcon />
          </IconButton>
          <Tooltip title="Open in New Tab">
            <span>
              <IconButton
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleMediaAction(item.invoiceMediaId, 'open')
                }}
                disabled={!item.invoiceMediaId}
              >
                <OpenInNewIcon />
              </IconButton>
            </span>
          </Tooltip>
        </div>
      </td>
    ),
  }

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setFilters((prev) => ({
        ...prev,
        search: searchInput,
      }))
      // Reset to first page when search changes
      setCurrentPage(0)
    }, 500) // 500ms debounce delay

    return () => clearTimeout(timeoutId)
  }, [searchInput]) // Only depend on searchInput, not filters.search to avoid infinite loop

  // Reset to first page when date range changes
  useEffect(() => {
    if (dateRange) {
      setCurrentPage(0)
    }
  }, [dateRange])

  // Initialize data
  useEffect(() => {
    if (entityListOverride) {
      setEntities(entityListOverride)
    } else {
      fetchEntities()
    }
  }, [entityListOverride, fetchEntities])

  // Refetch when page size changes (skip initial render)
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false
      return
    }
    fetchInvoices(0)
  }, [pageSize, fetchInvoices])

  // Calculate pagination
  const totalPages = Math.ceil(totalElements / pageSize)

  return (
    <Box sx={{ p: 2 }}>
      <Card elevation={2}>
        <CardContent>
          {/* Header with search and filters toggle */}
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}
          >
            <TextField
              size="small"
              label="Search invoices..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              sx={{ minWidth: 300 }}
              variant="outlined"
              placeholder="Search by invoice number, entity name..."
            />
            {(!hidePaymentStatusFilter || !hideTargetEntityIdFilter) && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => setShowFilters(!showFilters)}
                startIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                endIcon={<FilterListIcon />}
              >
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </Button>
            )}
          </Box>

          {/* Filters */}
          {(!hidePaymentStatusFilter || !hideTargetEntityIdFilter) && (
            <Collapse in={showFilters}>
              <Paper sx={{ p: 3, mb: 3 }} elevation={1}>
                <Grid container spacing={3}>
                  {!hideTargetEntityIdFilter && (
                    <Grid item xs={12} md={3}>
                      <CustomSelect
                        id="targetEntityIds"
                        label="Entity"
                        value={filters.targetEntityIds}
                        selectType={selectTypes.string}
                        onChangeEvent={(e) => handleFilterChange('targetEntityIds', e.target.value)}
                        options={entities}
                        getOptionLabel={(option) =>
                          targetEntityType === 'mentor'
                            ? option.name || option.username
                            : option.clinicBrandName || option.name
                        }
                        getOptionValue={(option) => option.id}
                      />
                      {/* <FormControl fullWidth size="small">
                    <InputLabel>Entity</InputLabel>
                    <Select
                      value={filters.targetEntityIds}
                      onChange={(e) => handleFilterChange('targetEntityIds', e.target.value)}
                      label="Entity"
                    >
                      <MenuItem value="">All</MenuItem>
                      {entities.map((entity) => (
                        <MenuItem key={entity.id} value={entity.id}>
                          {targetEntityType === 'mentor'
                            ? entity.name || entity.username
                            : entity.clinicBrandName || entity.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl> */}
                    </Grid>
                  )}

                  {!hidePaymentStatusFilter && (
                    <Grid item xs={12} md={3}>
                      <CustomSelect
                        id="paymentStatuses"
                        label="Payment Status"
                        value={filters.paymentStatuses}
                        selectType={selectTypes.string}
                        onChangeEvent={(e) => handleFilterChange('paymentStatuses', e.target.value)}
                        options={[
                          {
                            label: 'Pending',
                            value: 'pending',
                          },
                          {
                            label: 'Completed',
                            value: 'completed',
                          },
                          {
                            label: 'All',
                            value: null,
                          },
                        ]}
                        getOptionLabel={(option) => option?.label}
                        getOptionValue={(option) => option?.value}
                      />
                      {/* <FormControl fullWidth size="small">
                    <InputLabel>Payment Status</InputLabel>
                    <Select
                      value={filters.paymentStatus}
                      onChange={(e) => handleFilterChange('paymentStatus', e.target.value)}
                      label="Payment Status"
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                      <MenuItem value="failed">Failed</MenuItem>
                    </Select>
                  </FormControl> */}
                    </Grid>
                  )}

                  {/* <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Invoice Type</InputLabel>
                    <Select
                      value={filters.invoiceType}
                      onChange={(e) => handleFilterChange('invoiceType', e.target.value)}
                      label="Invoice Type"
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="provisional">Provisional</MenuItem>
                      <MenuItem value="final">Final</MenuItem>
                    </Select>
                  </FormControl>
                </Grid> */}

                  {/* <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Invoice Number"
                    value={filters.invoiceNumber}
                    onChange={(e) => handleFilterChange('invoiceNumber', e.target.value)}
                  />
                </Grid> */}

                  {/* <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                      Invoice Date Range
                    </Typography>
                    <DateRangePicker
                      onChange={setDateRange}
                      value={dateRange}
                      clearIcon={null}
                      calendarIcon={null}
                      format="dd/MM/yyyy"
                      rangeDivider=" to "
                    />
                  </Box>
                </Grid> */}

                  <Grid item xs={12} md={6}>
                    <Stack direction="row" spacing={2}>
                      <Button variant="contained" onClick={applyFilters} sx={{ minWidth: 120 }}>
                        Apply Filters
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={clearFilters}
                        startIcon={<ClearIcon />}
                        sx={{ minWidth: 120 }}
                      >
                        Clear Filters
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </Paper>
            </Collapse>
          )}

          {/* Sort Section */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="body2"
              sx={{ mb: 2, color: 'text.secondary', fontWeight: 'medium' }}
            >
              Sort By:
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
              {sortFields.map(({ field, label }) => (
                <Button
                  key={field}
                  variant={sortField === field ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => handleSortChange(field)}
                  endIcon={
                    sortField === field ? (
                      sortDirection === 'desc' ? (
                        <ArrowDownwardIcon fontSize="small" />
                      ) : (
                        <ArrowUpwardIcon fontSize="small" />
                      )
                    ) : null
                  }
                  sx={{
                    borderRadius: '20px',
                    textTransform: 'none',
                    minHeight: '32px',
                    fontSize: '0.875rem',
                    ...(sortField === field && {
                      backgroundColor: 'primary.main',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                    }),
                  }}
                >
                  {label}
                </Button>
              ))}
            </Stack>
          </Box>

          {/* Custom Pagination */}
          {totalPages > 1 && (
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {currentPage * pageSize + 1} to{' '}
                  {Math.min((currentPage + 1) * pageSize, totalElements)} of {totalElements} entries
                </Typography>
                <FormControl variant="standard" sx={{ minWidth: 120 }} size="small">
                  <InputLabel>Items Per page</InputLabel>
                  <Select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(e.target.value)
                      setCurrentPage(0)
                    }}
                    label="Per page"
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Pagination
                count={totalPages}
                page={currentPage + 1}
                onChange={(event, value) => fetchInvoices(value - 1)}
                color="primary"
                variant="outlined"
                shape="rounded"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
          {/* Table */}
          <CSmartTable
            columns={columns}
            items={invoices}
            loading={loading}
            onRowClick={handleRowClick}
            clickableRows
            tableProps={{
              hover: true,
              striped: true,
              responsive: true,
            }}
            itemsPerPage={1000000}
            itemsPerPageSelect={false}
            pagination={false}
            scopedColumns={scopedColumns}
            noItemsView={{
              noItems: 'No invoices found',
              noResults: 'No invoices match your search criteria',
            }}
          />
        </CardContent>
      </Card>

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            minWidth: 160,
          },
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        <MenuItem
          onClick={() => handleOpenMarkAsPaidDialog(selectedItem)}
          disabled={selectedItem?.paymentStatus === 'paid'}
        >
          <ListItemIcon>
            <CheckCircleIcon color="success" fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {selectedItem?.paymentStatus === 'paid' ? 'Paid' : 'Mark as Paid'}
          </ListItemText>
        </MenuItem>
      </Menu>

      {/* Conflict Resolution Dialog */}
      <InvoiceConflictDialog
        open={conflictDialogOpen}
        onClose={handleCloseConflictDialog}
        invoice={selectedInvoice}
        paymentInfo={pendingPaymentInfo}
        onMarkAsPaid={handleConflictResolved}
        onCancel={handleConflictCancellation}
      />

      {/* Mark as Paid Dialog */}
      <MarkAsPaidDialog
        open={markAsPaidDialogOpen}
        onClose={handleCloseMarkAsPaidDialog}
        invoice={selectedInvoice}
        onSuccess={handleMarkAsPaidSuccess}
      />

      {/* Invoice Details Dialog */}
      <Dialog
        maxWidth="lg"
        fullWidth
        open={showInvoiceDetailsDialog}
        onClose={handleCloseInvoiceDetailsDialog}
      >
        <InvoiceDetailsDialog
          onClose={handleCloseInvoiceDetailsDialog}
          invoice={selectedInvoice}
          bookedEventsListingApiSignature={bookedEventsListingApiSignature}
          prepareBookedEventsDataFromApiResponse={prepareBookedEventsDataFromApiResponse}
        />
      </Dialog>
    </Box>
  )
}

export default InvoiceListing
